/** The clutter that fills the ground between a town's buildings.
 *
 * The classic's town screens are DENSE. Castle-in.png has market stalls, tents,
 * fences, carts, hedges and a statue packed into every gap between its
 * dwellings, and the eye reads a settlement rather than a row of sprites
 * each standing alone in its own moat of painted lawn. A blind critic
 * measured ours as exactly that: "every building sits on its own contact
 * shadow island... no path leads anywhere, no wall or fence joins two
 * buildings". `townTracks.ts` put the ground between them; this puts the
 * things ON that ground.
 *
 * Twelve painterly props per faction (TOWNS/tools/props_catalog.py, cut by
 * build_props.py, sizes in `townPropArt.ts`), planted along the SAME stands
 * the buildings stand on:
 *
 *  - a prop is only ever placed between or just beside a BUILT building on
 *    its stand, which is the one span of that stand the layout has already
 *    proved is painted ground - so nothing is ever planted on open water,
 *    on fog, or off the shelf, and the clutter grows with the town;
 *  - the gap between two buildings is filled left to right with whatever
 *    fits, so a wide gap takes a stall and a fence and a narrow one takes a
 *    barrel;
 *  - it stands ON the stand's own ground curve at the stand's own scale, so
 *    a back-shelf cart is drawn at 0.4 and a front-row one at 1.3 and the
 *    depth ladder is the layout's, not a second opinion.
 *
 * Deterministic: which prop, how big and how far along are all hashed from
 * the faction, the stand and the prop's ordinal, so two runs of the same
 * town draw the same clutter and the screenshot harness sees one image.
 *
 * Pure: no DOM, no React, no kernel rule module, no randomness.
 */

import type { FactionLayout, LayoutPlot, LayoutSlot, LayoutStand } from "./townLayouts.js";
import { TOWN_LAYOUTS } from "./townLayouts.js";
import type { PropArt } from "./townPropArt.js";
import { TOWN_FOOTING_ART, TOWN_PROP_ART } from "./townPropArt.js";
import type { TownSlot } from "./townSlots.js";
import { TOWN_CANVAS_W } from "./townSlots.js";
import { standY } from "./townTracks.js";
import { hash01 } from "./hash01.js";

export interface TownProp {
  /** Atlas key, already namespaced: `town.<faction>.prop.<key>`. */
  readonly art: string;
  /** Horizontal centre and ground line, canvas pixels. */
  readonly x: number;
  readonly y: number;
  /** Drawn width and height on the canvas. */
  readonly w: number;
  readonly h: number;
  readonly band: TownSlot["band"];
  readonly flip: boolean;
  /**
   * The ground line this item is SORTED by, when that is not where it is
   * drawn. A footing is drawn below its building's feet (its own foot is
   * further down the slope) but must be painted BEFORE it, or the bank
   * covers the house standing on it.
   */
  readonly sortY?: number;
  /** The slot this item belongs to, when it is a footing under one. */
  readonly of?: string;
}

/**
 * How much of a building's own width counts as its footprint.
 *
 * Under 1: a prop may tuck a little way under the eaves of the house beside
 * it, which is what stops a row of props reading as a second, tidier row of
 * its own. The classic leans its hay carts against its walls.
 */
const FOOT = 0.88;
/** A prop may hang this far past the outermost building on its stand. */
const OVERHANG = 32;
/** Canvas px left between two props in the same gap, at scale 1. */
const SPACING = 6;
/** A gap narrower than this (at scale 1) is left empty. */
const MIN_GAP = 16;
/**
 * Below this drawn width a prop is a smudge on a hi-def painting.
 *
 * Measured on the first Saltreach shot: props on the middle shelves came out
 * 9-15 canvas px and read as flecks of dirt on the beach rather than as
 * lobster traps. A prop has to be big enough to be recognised or it is
 * noise, so the far shelves simply get fewer, larger things.
 */
const MIN_DRAWN = 15;
/**
 * At most this many props in one gap, however wide it is - per band.
 *
 * The near street is where the eye rests and where the classic packs its stalls; the
 * far shelf is a quarter of a mile off, where three carts are a village and
 * six are a rash of specks. So density falls with distance exactly as size
 * does.
 */
const PER_GAP: Readonly<Record<TownSlot["band"], number>> = Object.freeze({
  back: 3, middle: 6, front: 8,
});
/**
 * One prop for this much gap, at scale 1, per band.
 *
 * Round 5 filled a gap left to right from its left edge and stopped at
 * PER_GAP, so a 400 px plot took five props in its first 120 px and left
 * 280 px bare - which is precisely what the blind critic measured as
 * "680x240 px of empty pale ground with two road smears" on Prismgate and as
 * an empty rail corridor on Cogworks. A gap is now DIVIDED into as many
 * cells as it has room for and one prop is placed in each, so the clutter
 * spans the ground it is given instead of piling up at one end of it.
 */
const GAP_PER_PROP: Readonly<Record<TownSlot["band"], number>> = Object.freeze({
  back: 70, middle: 78, front: 88,
});
/** ...and this many on the whole screen. */
const MAX_PROPS = 104;

/**
 * How much of the footing's own height lies BELOW its flat top.
 *
 * A footing sprite is a foundation seen from the town's own camera: the
 * building stands on its top surface, and the rest of it - the bank's slope,
 * the pilings, the retaining wall, the plinth's steps - hangs below that. So
 * the sprite is drawn with its own foot this far under the building's ground
 * line, which is what makes the building sit ON it rather than behind it.
 */
const FOOTING_DROP: Readonly<Record<string, number>> = Object.freeze({
  bank: 0.62, piling: 0.66, shelf: 0.66, plinth: 0.56,
});
/** A footing is drawn this much wider than the building standing on it. */
const FOOTING_SPREAD = 1.18;

/** Deterministic 0..1 from an integer seed (townScenery's mixer). */

/** A stable integer for a string, so a faction name can seed the mixer. */
function strHash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/** The built footprints on one stand, left to right. */
function footprints(
  stand: LayoutStand,
  layout: FactionLayout,
  occupied: ReadonlySet<string>,
): readonly (readonly [number, number])[] {
  const here: LayoutSlot[] = layout.slots
    .filter((s) => occupied.has(s.name) && stand.slots.includes(s.name))
    .slice()
    .sort((a, b) => a.x - b.x);
  return here.map((s): readonly [number, number] => {
    const half = (s.w * s.scale * FOOT) / 2;
    return [s.x - half, s.x + half];
  });
}

/**
 * The empty spans on a stand: between the built buildings, and the overhang
 * either side of the outermost ones. Never the whole stand - an unbuilt half
 * of a shelf is not ground a town has reached yet.
 */
export function propGaps(
  stand: LayoutStand,
  layout: FactionLayout,
  occupied: ReadonlySet<string>,
): readonly (readonly [number, number])[] {
  const feet = footprints(stand, layout, occupied);
  if (feet.length === 0) return [];
  const out: (readonly [number, number])[] = [];
  const over = OVERHANG * stand.scale;
  const first = feet[0] as readonly [number, number];
  const last = feet[feet.length - 1] as readonly [number, number];
  out.push([Math.max(0, first[0] - over), first[0]]);
  for (let i = 0; i + 1 < feet.length; i++) {
    const a = feet[i] as readonly [number, number];
    const b = feet[i + 1] as readonly [number, number];
    if (b[0] > a[1]) out.push([a[1], b[0]]);
  }
  out.push([last[1], Math.min(TOWN_CANVAS_W, last[1] + over)]);
  return out.filter((g) => g[1] - g[0] >= MIN_GAP * stand.scale);
}

/**
 * The foundations under the buildings whose stand declares one.
 *
 * `town_layout._stand(footing=...)` names what the PAINTING does not give
 * the buildings on that stand: a bank where they stand on marsh, a piling
 * deck where they stand over open water, a cut rock shelf on a hillside, a
 * dressed plinth on a flooded platform. The blind critic's third high
 * defect was exactly this absence - "the plane is identical in front of and
 * behind the foot, there is no bank, no waterline, no reflection, no
 * piling" - and the backdrops are finished art that must not be repainted,
 * so the missing thing is drawn as a sprite under the building instead.
 *
 * Sorted and returned like props; `sortY` puts each one immediately before
 * its own building in the painter's order.
 */
export function townFootings(faction: string, occupied: ReadonlySet<string>): readonly TownProp[] {
  const layout: FactionLayout | undefined = TOWN_LAYOUTS[faction];
  const art: readonly PropArt[] | undefined = TOWN_FOOTING_ART[faction];
  if (layout === undefined || art === undefined || art.length === 0) return [];
  const out: TownProp[] = [];
  for (const slot of layout.slots) {
    const kind = slot.footing;
    if (kind === undefined || !occupied.has(slot.name)) continue;
    const piece = art.find((a) => a.key === `footing_${kind}`);
    if (piece === undefined) continue;
    const w = slot.w * slot.scale * FOOTING_SPREAD;
    const h = (piece.h / piece.w) * w;
    const drop = FOOTING_DROP[kind] ?? 0.6;
    out.push({
      art: `town.${faction}.prop.footing_${kind}`,
      x: Math.round(slot.x),
      y: Math.round(slot.y + h * drop),
      w, h,
      band: slot.band,
      flip: false,
      // just before the building it holds up, whatever else shares the row
      sortY: slot.y - 0.5,
      of: slot.name,
    });
  }
  return out;
}

/**
 * A PLOT is painted ground the layout declares on its own account, with no
 * slots on it (townLayouts.plots). It is filled end to end, because there
 * are no buildings on it to fill between.
 *
 * Round 4 planted props only in the gaps between BUILT buildings, which is
 * the one span the layout has proved is ground. The blind critic measured
 * what that leaves: "Prismgate's front-centre is roughly 680x240 screen px of
 * empty pale ground... about a third of the lower frame is bare", the same
 * in Cogworks's rail corridor and Castle's left third. A plot is the layout
 * saying "this too is ground", so the clutter can reach it.
 */
function plotStand(plot: LayoutPlot): LayoutStand {
  return { name: plot.name, band: plot.band, scale: plot.scale, path: plot.path, slots: [] };
}

/**
 * The town's props, given the names of the slots with something built.
 *
 * Sorted by ground line so the caller can merge them into the painter's
 * order the buildings are drawn in.
 */
export function townProps(faction: string, occupied: ReadonlySet<string>): readonly TownProp[] {
  const layout: FactionLayout | undefined = TOWN_LAYOUTS[faction];
  const art: readonly PropArt[] | undefined = TOWN_PROP_ART[faction];
  if (layout === undefined || art === undefined || art.length === 0) return [];
  const seed0 = strHash(faction);
  const out: TownProp[] = [];
  const areas: (readonly [LayoutStand, readonly (readonly [number, number])[]])[] = [];
  layout.stands.forEach((stand) => { areas.push([stand, propGaps(stand, layout, occupied)]); });
  // A plot is ground the TOWN has, so it grows clutter only once the town
  // exists: an unbuilt site is a landscape, not a settlement with a market
  // stall on it.
  if (occupied.size > 0) {
    for (const plot of layout.plots) {
      const stand = plotStand(plot);
      const xs = plot.path.map((p) => p[0]);
      areas.push([stand, [[Math.min(...xs), Math.max(...xs)] as const]]);
    }
  }
  areas.forEach(([stand, gaps], si) => {
    gaps.forEach((gap, gi) => {
      const span = gap[1] - gap[0];
      // How many things this gap wants: its own width over the band's
      // spacing, at least one and at most PER_GAP. Round 5 read PER_GAP
      // alone and filled from the left edge, so a 400 px plot took five
      // props in its first 120 px and left 280 px bare - the blind critic's
      // "680x240 px of empty pale ground" on Prismgate and the empty rail
      // corridor on Cogworks. A gap now asks for as many as it has room for.
      const want = Math.max(1, Math.min(
        PER_GAP[stand.band],
        Math.round(span / (GAP_PER_PROP[stand.band] * stand.scale)),
      ));
      // Pass one: WHICH props, packed, so a gap never claims more art than
      // it can hold (this is the round-4 rule and it is what keeps a narrow
      // alley's barrel).
      const picked: { piece: PropArt; w: number; h: number; seed: number }[] = [];
      let used = 0;
      for (let k = 0; k < want; k++) {
        const seed = seed0 + si * 7919 + gi * 104729 + k * 15485863;
        // A prop's size wanders by a fifth either way, so two barrels in one
        // street are not the same barrel drawn twice.
        const size = stand.scale * (0.86 + 0.28 * hash01(seed + 5));
        const room = span - used - SPACING * stand.scale * picked.length;
        const start = Math.floor(hash01(seed) * art.length);
        for (let j = 0; j < art.length; j++) {
          const cand = art[(start + j) % art.length] as PropArt;
          const w = cand.w * size;
          if (w >= MIN_DRAWN && w <= room) {
            picked.push({ piece: cand, w, h: cand.h * size, seed });
            used += w;
            break;
          }
        }
      }
      if (picked.length === 0) return;
      // Pass two: WHERE. The slack left over is dealt out evenly between
      // them and at both ends, so the clutter spans the ground it was given
      // instead of piling up at one end of it, and each one wanders inside
      // its own share so the result is scattered rather than ruled.
      const slack = Math.max(0, span - used) / (picked.length + 1);
      let cursor = gap[0];
      picked.forEach((p) => {
        const jitter = (hash01(p.seed + 31) - 0.5) * slack * 0.5;
        const x = cursor + slack + jitter + p.w / 2;
        cursor += slack + p.w;
        // Off the ground line by a hair, so a row of props is not a ruled
        // line and the nearer ones draw over the ones beside them.
        const dy = (hash01(p.seed + 11) - 0.35) * 4 * stand.scale;
        out.push({
          art: `town.${faction}.prop.${p.piece.key}`,
          x: Math.round(x),
          y: Math.round(standY(stand.path, x) + dy),
          w: p.w,
          h: p.h,
          band: stand.band,
          flip: hash01(p.seed + 23) < 0.35,
        });
      });
    });
  });
  // The cap falls on the far shelves first: a prop at the horizon is a
  // speck and a prop in the front street is half the reason the street is
  // there, so the nearest MAX_PROPS survive.
  const kept = out.slice().sort((a, b) => b.y - a.y).slice(0, MAX_PROPS);
  return kept.sort((a, b) => a.y - b.y || a.x - b.x);
}
